import { NewButton } from '@/components/basic';
import { DeleteResourceModal } from '@/components/partial/Modals/DeleteResourceModal';
import { GeneralPanel } from '@/components/partial/ResourceSettingsPanel/GeneralPanel';
import {
  EditResourceForm,
  getResourceType
} from '@/components/partial/ResourceSettingsPanel/ResourceSettingsPanel';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import { useS3Upload } from '@/hooks/useS3Upload';
import { api } from '@/lib/api/api';
import { hasEnabledDeleteResource } from '@/lib/featureFlags';
import { toEntity } from '@/lib/utils';
import { EditResourceRequest, IResource } from '@gettactic/api';
import { useLoadingToast } from '@gettactic/helpers/src/hooks/toasts/useLoadingToast';
import { useAreas } from '@gettactic/hooks';
import { useAuthenticated } from '@gettactic/hooks';
import { TrashIcon } from '@heroicons/react/outline';
import { BaseSyntheticEvent, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  resource: IResource;
  mutate: () => void;
  onSaved?: () => void;
};

export default function GeneralTab({ resource, mutate, onSaved }: Props) {
  const [deleteModal, setDeleteModal] = useState([] as IResource[]);
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const refSubmit = useRef<HTMLButtonElement>(null);
  const entity = resource
    ? toEntity(resource.type) || 'workspace'
    : 'workspace';
  const isMeetingRoom = entity === 'meeting_room';
  const s3Upload = useS3Upload('/api/uploads/map', true);
  const { uploadToS3 } = s3Upload;
  const { areas: areaList } = useAreas(resource?.office_id ?? null);
  const toast = useLoadingToast({
    loadingMessage: 'Applying changes...'
  });

  const parentIdOption = useMemo<EditResourceForm['parent_id']>(() => {
    return resource?.parent_id && areaList?.byId[resource.parent_id]
      ? {
          value: resource.parent_id,
          label: areaList.byId[resource.parent_id].name
        }
      : null;
  }, [resource, areaList]);

  const defaultValues: EditResourceForm = {
    name: resource?.name ?? '',
    label: resource.label,
    description: resource?.description,
    multiplicity: resource
      ? !isMeetingRoom && resource.total_slots > 1
        ? 'multiple'
        : 'single'
      : 'single',
    parent_id: parentIdOption,
    total_slots: resource?.total_slots ?? 1,
    maximum_slots_per_user: resource?.maximum_slots_per_user ?? null,
    is_open: resource.is_open,
    is_approvable: resource?.is_approvable ?? false,
    is_unavailable: !(resource?.is_available ?? true),
    map_url: resource?.map_url ?? null,
    map_file: null,
    amenities: []
  };
  const form = useForm<EditResourceForm>({
    defaultValues
  });
  const { handleSubmit, setValue, watch } = form;
  const isMultiple = watch('multiplicity') === 'multiple';

  const [loading, setLoading] = useState<boolean>(false);

  const onEditSubmit = async (
    data: EditResourceForm,
    event?: BaseSyntheticEvent<unknown, HTMLFormElement, HTMLFormElement>
  ) => {
    if (!resource) {
      return;
    }
    if (event?.target && event.target.id !== 'resourceSettingsPanel') {
      return;
    }

    setLoading(true);
    toast.startLoading();

    let mapUrlNew = data.map_url;
    if (data.map_file) {
      const uploadRes = await uploadToS3(data.map_file, {
        organizationId: authenticatedUser.organization?.id ?? '',
        resourceId: resource.id,
        officeId: resource.office_id
      });
      setValue('map_file', null);
      mapUrlNew = uploadRes.url;
    }

    const resourceType = getResourceType(entity, isMultiple);

    const request: EditResourceRequest = {
      type: resourceType,
      parent_id: data.parent_id?.value ? data.parent_id.value : null,
      name: data.name,
      total_slots:
        resource.type !== 'area'
          ? isMultiple || isMeetingRoom
            ? data.total_slots
            : 1
          : resource.total_slots,
      maximum_slots_per_user: data.maximum_slots_per_user ?? 1,
      is_open: data.is_open,
      is_approvable: data.is_approvable,
      is_available: !data.is_unavailable,
      // TODO: This is now an exposed field, but we don't have a way to edit on the UI
      // for now, we will set it to whatever it's already set to in the DB.
      is_shareable: resource.is_shareable,
      weight: resource.weight || 0,
      map_url: mapUrlNew,
      label: data.label,
      description: data.description
    };

    const { ok } = await api.client.organizations.resourcePut(
      resource.id,
      request
    );
    setLoading(false);
    if (ok) {
      onSaved?.();
      toast.success(resource ? 'Changes saved' : 'Created successfully');
    } else {
      toast.error("Sorry we couldn't save the changes");
    }
    mutate();
  };

  return (
    <div>
      <DeleteResourceModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />
      <form
        id="resourceSettingsPanel"
        className="flex flex-auto flex-col flex-nowrap overflow-hidden"
        onSubmit={handleSubmit(onEditSubmit)}
      >
        <div className="">
          <GeneralPanel
            resource={resource}
            refSubmit={refSubmit}
            entity={entity}
            areaList={areaList}
            defaultValues={defaultValues}
            form={form}
          />
        </div>
        <div className="my-4 flex flex-nowrap gap-3">
          <NewButton.Component
            ref={refSubmit}
            className="w-full sm:w-auto sm:flex-1"
            variant={NewButton.Variant.PrimarySquared}
            theme={NewButton.Theme.Primary}
            size={NewButton.Size.Medium}
            type="submit"
            form="resourceSettingsPanel"
            disabled={loading}
          >
            {loading ? <LoadingSpinner color="text-white" /> : <>Save</>}
          </NewButton.Component>
          {resource && hasEnabledDeleteResource(authenticatedUser) ? (
            <NewButton.Component
              className="w-full flex-none sm:w-auto"
              variant={NewButton.Variant.PrimarySquared}
              theme={NewButton.Theme.Red}
              size={NewButton.Size.Medium}
              disabled={loading}
              onClick={(ev) => {
                ev.preventDefault();
                setDeleteModal([resource]);
              }}
            >
              <TrashIcon className="h-6 w-6" />
            </NewButton.Component>
          ) : null}
        </div>
      </form>
    </div>
  );
}
